'use client';

/**
 * Shared crew legend + filter chips used by FullSchedule (all 3 views) and
 * MiniMonthCalendar standalone. Click a chip to filter; click again to clear.
 * The pencil icon opens an inline editor for renaming/recoloring (per-tenant
 * localStorage; see lib/crew-display).
 */

import { useState } from 'react';
import { Pencil } from 'lucide-react';
import {
  setCrewOverride, clearCrewOverride,
  type CrewOverride,
} from '@/lib/crew-display';
import { BrandedColorPicker } from '@/components/ui/BrandedColorPicker';

interface CrewLegendProps {
  tenantId: string | null;
  crews: string[];
  overrides: Record<string, CrewOverride>;
  paletteFallback: Record<string, string>;
  crewFilter: string | null;
  onCrewFilterChange: (crew: string | null) => void;
  editable?: boolean;
}

export function CrewLegend({
  tenantId, crews, overrides, paletteFallback,
  crewFilter, onCrewFilterChange, editable = true,
}: CrewLegendProps) {
  const [editingCrew, setEditingCrew] = useState<string | null>(null);

  if (crews.length === 0) return null;

  return (
    <div className="border-b border-[--border-primary] bg-gray-50/60 dark:bg-[#111]/60">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
        <span className="text-[10px] uppercase tracking-wide font-semibold text-[--text-tertiary] mr-1">Crew</span>
        <button
          onClick={() => onCrewFilterChange(null)}
          className={`px-2 py-0.5 text-[11px] rounded-full font-medium transition-colors ${
            crewFilter === null
              ? 'bg-[--text-primary] text-white'
              : 'bg-gray-200 dark:bg-[#2a2a2a] text-[--text-secondary] hover:text-[--text-primary]'
          }`}
        >
          All
        </button>
        {crews.map(crewKey => {
          const active = crewFilter === crewKey;
          const color = overrides[crewKey]?.color || paletteFallback[crewKey] || '#00C2A8';
          const label = overrides[crewKey]?.name?.trim() || crewKey;
          return (
            <div key={crewKey} className="flex items-center gap-1">
              <button
                onClick={() => onCrewFilterChange(active ? null : crewKey)}
                className={`flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium border rounded-full transition-colors ${
                  active
                    ? 'bg-[--text-primary] text-white border-transparent'
                    : 'bg-white dark:bg-[#1a1a1a] text-[--text-secondary] hover:text-[--text-primary] border-[--border-primary]'
                }`}
                title={`Filter to ${label}`}
              >
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                {label}
              </button>
              {editable && (
                <button
                  onClick={() => setEditingCrew(editingCrew === crewKey ? null : crewKey)}
                  className="w-6 h-6 flex items-center justify-center rounded-full text-[--text-tertiary] hover:text-[--text-primary] hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors"
                  title="Rename or recolor"
                  aria-label={`Edit display for ${label}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {editingCrew && editable && (
        <CrewEditorRow
          crewKey={editingCrew}
          override={overrides[editingCrew] || {}}
          paletteFallback={paletteFallback}
          onChange={(patch) => setCrewOverride(tenantId, editingCrew, patch)}
          onReset={() => { clearCrewOverride(tenantId, editingCrew); setEditingCrew(null); }}
          onClose={() => setEditingCrew(null)}
        />
      )}
    </div>
  );
}

interface CrewEditorRowProps {
  crewKey: string;
  override: CrewOverride;
  paletteFallback: Record<string, string>;
  onChange: (patch: CrewOverride) => void;
  onReset: () => void;
  onClose: () => void;
}

// Quick swatches shown inline. Operators pick from these 80% of the time;
// the full palette + custom hex picker live behind the More popout.
const QUICK_SWATCHES = ['#00C2A8', '#F59E0B', '#EF4444', '#3B82F6'];

function CrewEditorRow({ crewKey, override, paletteFallback, onChange, onReset, onClose }: CrewEditorRowProps) {
  const [name, setName] = useState(override.name ?? '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const currentColor = override.color || paletteFallback[crewKey] || '#00C2A8';

  return (
    <div className="px-4 py-3 border-t border-[--border-primary] bg-white dark:bg-[#1a1a1a] flex flex-wrap items-center gap-3 relative">
      <span className="text-[11px] text-[--text-primary] font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-[#222] border border-[--border-primary]">{crewKey}</span>
      <input
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        onBlur={() => onChange({ name })}
        placeholder={`Rename ${crewKey}`}
        className="px-2 py-1 text-[11px] rounded-md border border-[--border-primary] bg-white dark:bg-[#1a1a1a] text-[--text-primary] focus:outline-none focus:border-[#00C2A8] w-40"
      />
      <div className="flex items-center gap-1.5">
        {QUICK_SWATCHES.map(c => (
          <button
            key={c}
            onClick={() => onChange({ color: c })}
            className={`w-5 h-5 rounded-full border-2 transition-transform ${currentColor === c ? 'border-[--text-primary] scale-110' : 'border-transparent hover:scale-105'}`}
            style={{ backgroundColor: c }}
            aria-label={`Set color to ${c}`}
          />
        ))}
        <button
          onClick={() => setPickerOpen(v => !v)}
          className={`w-5 h-5 rounded-full border-2 transition-transform flex items-center justify-center text-[11px] font-bold ${pickerOpen ? 'border-[#00C2A8] text-[#00C2A8]' : 'border-[--border-primary] text-[--text-secondary] hover:text-[--text-primary]'}`}
          aria-label="More colors"
          title="More colors"
        >
          +
        </button>
        {/* Active-color preview chip — confirms what's actually applied,
            even when the active color isn't one of the 4 quick swatches. */}
        <span
          className="w-5 h-5 rounded-full border border-[--border-primary] ml-1"
          style={{ backgroundColor: currentColor }}
          title={`Current color: ${currentColor}`}
          aria-hidden
        />
      </div>
      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={onReset}
          className="px-2 py-1 text-[11px] rounded-md text-[--text-secondary] hover:text-[--text-primary] hover:bg-gray-200 dark:hover:bg-[#222] transition-colors"
        >
          Reset
        </button>
        <button
          onClick={onClose}
          className="px-2 py-1 text-[11px] font-semibold rounded-md bg-[#00C2A8] text-white hover:bg-[#00a48f] transition-colors"
        >
          Done
        </button>
      </div>

      {pickerOpen && (
        <ColorPickerPopout
          currentColor={currentColor}
          onPick={(c) => onChange({ color: c })}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

interface ColorPickerPopoutProps {
  currentColor: string;
  onPick: (c: string) => void;
  onClose: () => void;
}

function ColorPickerPopout({ currentColor, onPick, onClose }: ColorPickerPopoutProps) {
  return (
    <div className="absolute inset-0 z-30">
      <div className="absolute inset-0" onClick={onClose} />
      <div
        className="absolute right-4 top-full mt-2 w-[320px] rounded-2xl border border-[--border-primary] bg-white dark:bg-[#1a1a1a] shadow-2xl p-4 z-40"
        onClick={(e) => e.stopPropagation()}
      >
        <BrandedColorPicker
          value={currentColor}
          onChange={onPick}
          onClose={onClose}
          footnote="Saved colors apply universally — the schedule and the inbox calendar update instantly."
        />
      </div>
    </div>
  );
}
